import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import gql from 'graphql-tag';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuthentication } from '~auth/useAuthentication';
import { useS3Upload } from '~utils/useS3Upload';
import {
  useZteUploadRequestMutation,
  ZteUploadRequestMutation,
} from './__generated__/ZteCSVUploadModal.graphql';
import { CustomerSelect } from './CustomerSelect';
import { CSVInput } from './FileUploadButton';
import { OrganizationSelect } from './OrganizationSelect';

interface Props {
  isOpen: React.ComponentProps<typeof Modal>['isOpen'];
  onClose: React.ComponentProps<typeof Modal>['onClose'];
  onSuccess: (data: ZteUploadRequestMutation['zteUploadRequest']) => void;
}

const formSchema = z.object({
  organization: z.string(),
  customer: z.string().optional(),
  file: z.instanceof(File, { message: 'File is required' }),
});

type FormValues = z.TypeOf<typeof formSchema>;

export function ZteCSVUploadModal({ isOpen, onClose, onSuccess }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent mt="150px">
        <ZteCSVUploadModalContent onClose={onClose} isOpen={isOpen} onSuccess={onSuccess} />
      </ModalContent>
    </Modal>
  );
}

export function ZteCSVUploadModalContent({
  onClose,
  onSuccess,
}: {
  onClose: Props['onClose'];
  isOpen: Props['isOpen'];
  onSuccess: Props['onSuccess'];
}) {
  const [ZteCSVUpload] = useZteUploadRequestMutation();
  const {
    setValue,
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      file: null as unknown as File,
    },
  });
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [uploadedCSVState, setUploadedCSVState] = useState<File | null>(null);
  const inputRefCSV = useRef<HTMLInputElement>(null);

  const toast = useToast({ position: 'bottom-right' });
  useEffect(() => {
    register('file', { required: true });
  }, [register]);
  const { upload } = useS3Upload();

  const setUploadedCSV = (file: File | null) => {
    if (!file) {
      setUploadedCSVState(null);
      setValue('file', null as unknown as File, { shouldDirty: true });
      if (inputRefCSV.current) {
        inputRefCSV.current.value = '';
      }
      return;
    }

    setUploadedCSVState(file);
    setValue('file', file, { shouldDirty: true });
  };

  const auth = useAuthentication();

  const onSubmit = useCallback(
    async ({ file }: FormValues) => {
      try {
        if (!file) {
          setError('file', {
            type: 'api',
            message: 'A CSV file must be selected before submitting.',
          });
          return;
        }
        const { data } = await ZteCSVUpload({
          variables: {
            input: {
              organizationId: selectedOrgId || '',
              customerId: selectedCustomerId,
              fileName: file.name,
              email: auth.user.email || '',
            },
          },
        });

        if (!data || !data.zteUploadRequest.uploadUrl) {
          throw new Error('CSV Upload failed');
        }
        await upload(data.zteUploadRequest.uploadUrl, file, '');

        onSuccess(data.zteUploadRequest);
      } catch (err) {
        toast({
          title: 'Could not upload csv fie',
          description: 'Unknown error',
          status: 'error',
        });
      }
    },
    [
      ZteCSVUpload,
      onSuccess,
      selectedCustomerId,
      selectedOrgId,
      setError,
      toast,
      upload,
      auth.user.email,
    ],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>Add new CSV File</ModalHeader>
      <ModalBody>
        <VStack w="full" align="left" spacing={5}>
          <Box>
            <FormControl isInvalid={Boolean(errors.organization)} isRequired>
              <FormLabel>Select Organization:</FormLabel>
              <OrganizationSelect
                tabIndex={3}
                onChange={(organization) => {
                  setValue('organization', organization.label ?? '');
                  setSelectedOrgId(organization.value);
                }}
              />
              <FormErrorMessage>{errors.organization?.message}</FormErrorMessage>
            </FormControl>
          </Box>
          <Box>
            <FormControl isInvalid={Boolean(errors.customer)}>
              <FormLabel>Select Customer:</FormLabel>
              <CustomerSelect
                tabIndex={3}
                organizationId={selectedOrgId}
                onChange={(customer) => {
                  setValue('customer', customer.label ?? '');
                  setSelectedCustomerId(customer.value);
                }}
              />
              <FormErrorMessage>{errors.customer?.message}</FormErrorMessage>
            </FormControl>
          </Box>
          <Box>
            <FormControl isInvalid={!!errors.file} isRequired>
              <FormLabel>Attach CSV File:</FormLabel>
              <CSVInput
                ref={inputRefCSV}
                onChange={(file) => {
                  setUploadedCSV(file);
                  clearErrors('file');
                }}
                onError={(err) => {
                  toast({
                    title: err.message,
                    status: 'error',
                  });
                }}
                fileName={uploadedCSVState?.name}
                clearLabel="Remove CSV"
              />
              <FormErrorMessage>{errors.file?.message}</FormErrorMessage>
            </FormControl>
          </Box>
        </VStack>
        <ModalFooter>
          <Box>
            <Button variant="ghost" isDisabled={isSubmitting} onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Box>
            <Button
              type="submit"
              variant="solid"
              isDisabled={isSubmitting || !uploadedCSVState || !selectedOrgId}
              isLoading={isSubmitting}
              _disabled={{
                bg: '#BDBDBD',
                borderColor: '#BDBDBD',
                // color: 'gray.600',
                cursor: 'not-allowed',
                _hover: {
                  bg: '#BDBDBD',
                },
              }}
            >
              Upload
            </Button>
          </Box>
        </ModalFooter>
      </ModalBody>
    </form>
  );
}

ZteCSVUploadModal.graphql = {
  mutations: {
    zteUploadRequest: gql`
      mutation zteUploadRequest($input: ZteUploadRequestInput!) {
        zteUploadRequest(input: $input) {
          uploadUrl
        }
      }
    `,
  },
};
